import React, { useMemo, Suspense, useState, useEffect } from 'react';

// Dynamically import react-quill (and its CSS) only when the editor is actually rendered
const QuillLoader = React.lazy(() => import('react-quill'));
let quillCSSLoaded = false;
const ensureQuillCSS = () => {
    if (quillCSSLoaded) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://cdn.jsdelivr.net/npm/react-quill@2.0.0/dist/quill.snow.css';
    document.head.appendChild(link);
    quillCSSLoaded = true;
};

// We defer icon customization until after Quill is loaded
const registerCustomIcons = (Quill) => {
    try {
        if (!Quill) return;
        const icons = Quill.import('ui/icons');
        if (icons && !icons.undo) {
            icons.undo = '<span class="ql-custom-icon" aria-hidden="true">↺</span>';
        }
        if (icons && !icons.redo) {
            icons.redo = '<span class="ql-custom-icon" aria-hidden="true">↻</span>';
        }
    } catch(e) {
        // Fail silently
    }
};

// Register custom undo/redo icons & ensure history module is present
// Guard so we don't overwrite if hot reloading
try {
    const icons = Quill.import('ui/icons');
    if (!icons.undo) {
        icons.undo = '<span class="ql-custom-icon" aria-hidden="true">↺</span>';
    }
    if (!icons.redo) {
        icons.redo = '<span class="ql-custom-icon" aria-hidden="true">↻</span>';
    }
} catch (e) {
    // Fail silently if Quill not ready yet
}

const WysiwygEditor = ({
    value,
    onChange,
    name,
    placeholder = "Enter text...",
    height = "200px",
    ...props
}) => {
    const handleChange = (content) => {
        // Create a synthetic event object that matches the expected structure
        const syntheticEvent = {
            target: {
                name: name,
                value: content
            }
        };
        onChange(syntheticEvent);
    };

    // Quill modules configuration with undo/redo + history
    const modules = useMemo(() => ({
        toolbar: {
            container: [
                ['undo', 'redo'],
                [{ header: [1, 2, 3, false] }],
                ['bold', 'italic', 'underline', 'strike'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                [{ indent: '-1' }, { indent: '+1' }],
                [{ color: [] }, { background: [] }],
                [{ align: [] }],
                ['link', 'image'],
                ['clean']
            ],
            handlers: {
                undo() {
                    this.quill.history.undo();
                },
                redo() {
                    this.quill.history.redo();
                }
            }
        },
        history: {
            delay: 1000,
            maxStack: 100,
            userOnly: true
        }
    }), []);

    // Quill formats
    const formats = [
        'header', 'bold', 'italic', 'underline', 'strike',
        'list', 'bullet', 'indent',
        'color', 'background', 'align',
        'link', 'image'
    ];

    const [quillModule, setQuillModule] = useState(null);

    useEffect(() => {
        let mounted = true;
        // Preload dynamically (optional) on mount
        ensureQuillCSS();
        import('react-quill').then(mod => {
            if (!mounted) return;
            setQuillModule(mod);
            registerCustomIcons(mod.Quill);
        });
        return () => { mounted = false; };
    }, []);

    const QuillComponent = quillModule ? quillModule.default : null;

    return (
        <div style={{ height: height }}>
            {!QuillComponent && (
                <div style={{fontSize: '0.85rem', color: '#666'}}>Loading editor...</div>
            )}
            {QuillComponent && (
                <QuillComponent
                    value={value || ''}
                    onChange={handleChange}
                    placeholder={placeholder}
                    modules={modules}
                    formats={formats}
                    style={{ height: `calc(${height} - 42px)` }}
                    {...props}
                />
            )}
        </div>
    );
};

export default WysiwygEditor;
